import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiUsers, FiSearch, FiEye, FiAlertCircle, FiUserX, FiBook } from 'react-icons/fi';
import './MaintenanceKelas.css';

const MaintenanceKelas = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    filterType: 'all', // all, no-lecturer, no-students
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState({});
  const [stats, setStats] = useState({
    total: 0,
    noLecturer: 0,
    noStudents: 0
  });

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get('/api/classes/maintenance/stats');
      setStats(res.data.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  const fetchClasses = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const res = await axios.get(`/api/classes/maintenance?${queryParams}`);
      setClasses(res.data.data);
      setPagination(res.data.pagination);
    } catch (error) {
      toast.error('Gagal mengambil data kelas');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchClasses();
    fetchStats();
  }, [fetchClasses, fetchStats]);

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
      page: 1
    });
  };

  const handlePageChange = (newPage) => {
    setFilters({ ...filters, page: newPage });
  };

  const getFilterTypeLabel = (type) => {
    switch(type) {
      case 'no-lecturer': return 'Tanpa Dosen';
      case 'no-students': return 'Tanpa Peserta';
      default: return 'Semua';
    }
  };

  return (
    <div className="maintenance-kelas-page">
      {/* Header */}
      <div className="page-header modern-header">
        <div className="header-background"></div>
        <div className="header-content">
          <div className="header-left">
            <div className="header-icon-wrapper">
              <FiBook className="header-icon" />
            </div>
            <div className="header-text">
              <h1 className="header-title">Maintenance Kelas</h1>
              <p className="header-subtitle">Monitoring dan pemeliharaan data kelas perkuliahan</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card stat-card-total">
          <div className="stat-icon">
            <FiBook />
          </div>
          <div className="stat-info">
            <p className="stat-label">Total Kelas</p>
            <h3 className="stat-value">{stats.total}</h3>
          </div>
        </div>
        <div className="stat-card stat-card-warning">
          <div className="stat-icon">
            <FiUserX />
          </div>
          <div className="stat-info">
            <p className="stat-label">Tanpa Dosen</p>
            <h3 className="stat-value">{stats.noLecturer}</h3>
          </div>
        </div>
        <div className="stat-card stat-card-danger">
          <div className="stat-icon">
            <FiAlertCircle />
          </div>
          <div className="stat-info">
            <p className="stat-label">Tanpa Peserta</p>
            <h3 className="stat-value">{stats.noStudents}</h3>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card filters-card">
        <div className="filters-grid">
          <div className="form-group">
            <div className="search-box">
              <FiSearch />
              <input
                type="text"
                name="search"
                placeholder="Cari nama kelas atau kode mata kuliah..."
                value={filters.search}
                onChange={handleFilterChange}
                className="form-control"
              />
            </div>
          </div>

          <div className="form-group">
            <select
              name="filterType"
              value={filters.filterType}
              onChange={handleFilterChange}
              className="form-control"
            >
              <option value="all">Semua Kelas</option>
              <option value="no-lecturer">Tanpa Dosen</option>
              <option value="no-students">Tanpa Peserta</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="spinner"></div>
        ) : classes.length === 0 ? (
          <div className="empty-state">
            <FiBook size={48} />
            <p>Tidak ada data kelas yang ditemukan</p>
          </div>
        ) : (
          <>
            <div className="table-header-info">
              <p>
                Menampilkan <strong>{getFilterTypeLabel(filters.filterType)}</strong>: 
                <strong> {pagination.total}</strong> kelas
              </p>
            </div>
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Nama Kelas</th>
                    <th>Kode MK</th>
                    <th>Mata Kuliah</th>
                    <th>Semester</th>
                    <th>Dosen Pengajar</th>
                    <th>Jumlah Peserta</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {classes.map((cls, index) => (
                    <tr key={cls._id}>
                      <td>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                      <td>
                        <strong>{cls.className || '-'}</strong>
                      </td>
                      <td>
                        <code>{cls.courseCode || '-'}</code>
                      </td>
                      <td>{cls.courseName || '-'}</td>
                      <td>{cls.semester || '-'}</td>
                      <td>
                        {cls.hasLecturer ? (
                          <span className="badge badge-success">
                            <FiUsers /> Ada Dosen
                          </span>
                        ) : (
                          <span className="badge badge-danger">
                            <FiUserX /> Tanpa Dosen
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`student-count ${cls.studentCount === 0 ? 'zero' : ''}`}>
                          {cls.studentCount} peserta
                        </span>
                      </td>
                      <td>
                        {!cls.hasLecturer || cls.studentCount === 0 ? (
                          <span className="badge badge-warning">
                            <FiAlertCircle /> Perlu Perhatian
                          </span>
                        ) : (
                          <span className="badge badge-success">Normal</span>
                        )}
                      </td>
                      <td>
                        <div className="table-actions">
                          <Link
                            to={`/kelas/${cls._id}`}
                            className="table-btn table-btn-view"
                            title="Detail"
                          >
                            <FiEye />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="table-pagination">
                <div className="table-pagination-info">
                  Halaman {pagination.page} dari {pagination.pages} ({pagination.total} data)
                </div>
                <div className="table-pagination-buttons">
                  <button
                    className="table-pagination-button"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </button>
                  <button
                    className="table-pagination-button"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MaintenanceKelas;
